import { CdkStepper } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  collectFormlyValidationErrors,
  FormlyValidationError,
  getFormlyValidationErrorsSummary,
} from '../custom-validations/formly-validation-errors.util';
import { VerticalStepperService } from '../../../services/vertical-stepper/vertical-stepper.service';
import { Directionality } from '@angular/cdk/bidi';
import { Router } from '@angular/router';
import { IEnvironment } from '../../../models/environment.model';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'irembogov-custom-vertical-stepper',
  templateUrl: './custom-vertical-stepper.component.html',
  styleUrls: ['./custom-vertical-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: CdkStepper, useExisting: CustomVerticalStepperComponent },
  ],
})
export class CustomVerticalStepperComponent
  extends CdkStepper
  implements OnDestroy
{
  @Input() fields: FormlyFieldConfig[] | undefined;
  @ViewChild('hiddenSubmitButton', { static: true })
  hiddenSubmitButton?: ElementRef<HTMLButtonElement>;
  draftIsSaving = false;
  draftButtonText = 'Save Draft';
  shouldShowDraft = true;
  public environment: IEnvironment;
  validationErrors: FormlyValidationError[] = [];
  private subscriptions = new Subscription();

  get isInNestedContext(): boolean {
    const hasParentServiceRender =
      this.elementRef.nativeElement.closest(
        'irembogov-single-service-render'
      ) !== null;
    return hasParentServiceRender;
  }

  shouldShowContinueButton(isLast: boolean): boolean {
    const inNestedContext = this.isInNestedContext;
    return inNestedContext ? !isLast : true;
  }

  shouldShowSaveDraftButton(isLast: boolean): boolean {
    if (!this.shouldShowDraft) return false;
    const inNestedContext = this.isInNestedContext;
    return inNestedContext ? !isLast : true;
  }

  isStepCompleted(stepIndex: number): boolean {
    return this.selectedIndex > stepIndex;
  }

  shouldShowEditButton(stepIndex: number): boolean {
    return this.isStepCompleted(stepIndex);
  }

  shouldShowStepIcon(stepIndex: number): boolean {
    return !this.isStepCompleted(stepIndex);
  }

  getStepClasses(stepIndex: number): Record<string, boolean> {
    return {
      completed__step: this.isStepCompleted(stepIndex),
      active__step: this.selectedIndex === stepIndex,
      hidden__step: this.selectedIndex !== stepIndex,
    };
  }

  isStepCollapsed(stepIndex: number): boolean {
    return this.selectedIndex !== stepIndex;
  }

  constructor(
    dir: Directionality,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private verticalStepperService: VerticalStepperService,
    private router: Router,
    private translate: TranslateService,
    @Inject('environment') environment: IEnvironment
  ) {
    super(dir, changeDetectorRef, elementRef);
    this.environment = environment;

    this.subscriptions.add(
      this.verticalStepperService.draftIsSavingObservable$.subscribe({
        next: (toggle: boolean) => {
          this.draftIsSaving = toggle;
          this.draftButtonText = toggle ? 'Saving...' : 'Save Draft';
          changeDetectorRef.markForCheck();
        },
      })
    );

    this.subscriptions.add(
      this.verticalStepperService.shouldShowDraftObservable$.subscribe({
        next: (toggle: boolean) => {
          this.shouldShowDraft = toggle;
          changeDetectorRef.markForCheck();
        },
      })
    );

    this.subscriptions.add(
      this.verticalStepperService.validateNestedStepperObservable$.subscribe({
        next: () => {
          const isLastStep = this.selectedIndex === this.steps.length - 1;
          const hasHiddenButton =
            this.isInNestedContext && !this.shouldShowContinueButton(true);

          // Only respond if this nested stepper is inside the currently active parent step
          const isInsideActiveStep =
            this.elementRef.nativeElement.closest('.card.active__step') !==
            null;

          if (hasHiddenButton && isLastStep && isInsideActiveStep) {
            const currentStep = this.fields?.[this.selectedIndex];
            if (currentStep) {
              if (this.environment.CHANGE_REQUEST_TESTING_ENABLED) {
                const invalidFields = collectFormlyValidationErrors(
                  currentStep,
                  this.translate
                );
                console.log('[NESTED STEPPER] Validation:', {
                  isValid: this.isValid(currentStep),
                  invalidFields:
                    invalidFields.length > 0
                      ? invalidFields
                      : 'All fields valid',
                });
              }

              // Validate and show errors if invalid
              if (!this.isValid(currentStep)) {
                this.validationErrors =
                  this.collectValidationErrors(currentStep);
                this.changeDetectorRef.detectChanges();

                const card =
                  this.elementRef.nativeElement.querySelector(
                    '.card.active__step'
                  );
                if (card)
                  card.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }

              this.verticalStepperService.triggerChildNextClick(
                this.selectedIndex,
                isLastStep,
                currentStep
              );
            }
          }
        },
      })
    );
  }

  onNext(field: FormlyFieldConfig) {
    this.validationErrors = [];

    if (this.environment.CHANGE_REQUEST_TESTING_ENABLED) {
      const simplifyField = (f: FormlyFieldConfig): any => ({
        id: f.id,
        key: f.key,
        props: f.props,
        status: f.formControl?.status,
        validators: {
          sync: f.validators ? Object.keys(f.validators) : [],
          async: f.asyncValidators ? Object.keys(f.asyncValidators) : [],
          messages: f.validation?.messages ?? {},
          errors: f.formControl?.errors ?? {},
        },
        fieldGroup: f.fieldGroup?.map(simplifyField) ?? undefined,
      });
      const simplified = simplifyField(field);
      console.log(
        'Testing enabled and logging the form for debugging purposes',
        this.environment.CHANGE_REQUEST_TESTING_ENABLED,
        simplified
      );
    }

    if (!this.isValid(field)) {
      this.validationErrors = this.collectValidationErrors(field);
      this.verticalStepperService.emitStepValidationFailed(
        getFormlyValidationErrorsSummary([field], this.translate)
      );

      this.changeDetectorRef.detectChanges();

      const card =
        this.elementRef.nativeElement.querySelector('.card.active__step');
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    let responded = false;
    const sub =
      this.verticalStepperService.childNextClickedObservable$.subscribe({
        next: ({ field: childField }) => {
          responded = true;
          sub.unsubscribe();
          if (this.isValid(childField)) {
            this.proceedToNextStep(field);
          }
        },
      });

    this.verticalStepperService.requestNestedStepperValidation(
      this.selectedIndex,
      () => undefined
    );

    if (!responded) {
      sub.unsubscribe();
      this.proceedToNextStep(field);
    }
  }

  private proceedToNextStep(field: FormlyFieldConfig): void {
    const isLastStep = this.selectedIndex === this.steps.length - 1;

    this.verticalStepperService.triggerChildNextClick(
      this.selectedIndex,
      isLastStep,
      field
    );

    const isHandled = this.verticalStepperService.handleLastStepBehavior(
      this.selectedIndex,
      isLastStep,
      field
    );

    if (isHandled) {
      return;
    }

    if (isLastStep) {
      this.hiddenSubmitButton?.nativeElement?.click();
      return;
    }

    this.next();
    this.verticalStepperService.triggerDraftSaving(field.model);
  }

  goBack(index: number) {
    if (this.selectedIndex > index) {
      this.previous();
    }
  }

  cancel() {
    this.verticalStepperService.triggerDraftSavingBoolean(false);
    this.router.navigate(['/']);
  }

  isValid(field: FormlyFieldConfig): boolean {
    let isTreeValid = true;

    // Skip validation completely if the root field itself is hidden
    if (field.hide) {
      return true;
    }

    const traverse = (f: FormlyFieldConfig): void => {
      // Immediately skip traversing or validating hidden fields
      if (f.hide) {
        return;
      }
      if (
        f.type === 'section' ||
        f.type === 'block' ||
        f.type === 'formly-group' ||
        f.type === 'step'
      ) {
        f.fieldGroup?.forEach(traverse);
        return;
      }

      if (f.formControl) {
        // Force Angular to re-evaluate the validation rules
        f.formControl.updateValueAndValidity({ emitEvent: false });

        // Safely check if it is invalid
        if (f.formControl.invalid) {
          isTreeValid = false;

          if (typeof f.formControl.markAllAsTouched === 'function') {
            f.formControl.markAllAsTouched();
          } else {
            f.formControl.markAsTouched({ onlySelf: false });
          }
        }
      }

      f.fieldGroup?.forEach(traverse);
    };

    traverse(field);

    if (field.formControl && !field.hide) {
      field.formControl.updateValueAndValidity({ emitEvent: false });
      if (field.formControl.invalid) {
        isTreeValid = false;
      }
    }

    return isTreeValid;
  }

  handleDraft(field: FormlyFieldConfig): void {
    this.verticalStepperService.triggerDraftSaving(field.model);
    this.verticalStepperService.triggerDraftSavingBoolean(true);
  }

  private collectValidationErrors(
    field: FormlyFieldConfig
  ): { label: string; messages: string[] }[] {
    return collectFormlyValidationErrors(field, this.translate);
  }

  override ngOnDestroy() {
    this.subscriptions.unsubscribe();
    super.ngOnDestroy();
  }
}
